/**
 * Service for managing waitlist users and their integration with existing systems
 * 
 * WAITLIST USER IDENTIFICATION:
 * - Waitlist users are identified by a SINGLE field: status === 'waitlist'
 * - This is the ONLY source of truth for determining waitlist status
 * - Waitlist users should ONLY access /waitlist route
 * - Waitlist users should NEVER trigger onboarding flow
 */

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from "firebase/firestore";
import { db } from "./firebase-config";

/**
 * Create waitlist user profile with proper integration
 * This function is called after email verification
 * NOTE: This function is now deprecated - waitlist users get real Firebase Auth accounts
 * and profiles are created directly in the completeAccountCreation Cloud Function
 */
export const createWaitlistUserProfile = async (userData, verificationData) => {
  try {
    console.log('Creating waitlist user profile:', userData.userId);
    
    const profileRef = doc(db, "user_profiles", userData.userId);
    
    // Create comprehensive user profile for waitlist user
    const userProfileData = {
      uid: userData.userId,
      userId: userData.userId,
      email: userData.email,
      displayName: userData.displayName,
      emailVerified: true,
      status: 'waitlist', // Key field for waitlist users
      basicInfo: {
        firstName: verificationData.firstName,
        lastName: verificationData.lastName,
        email: userData.email,
        linkedinUrl: verificationData.linkedinUrl,
        name: userData.displayName
      },
      waitlistMetadata: {
        joinedAt: serverTimestamp(),
        source: verificationData.source || 'waitlist_signup',
        verifiedAt: serverTimestamp(),
        referredBy: verificationData.referredBy || '',
        originalSubmissionData: {
          firstName: verificationData.firstName,
          lastName: verificationData.lastName,
          email: userData.email,
          linkedinUrl: verificationData.linkedinUrl,
          referredBy: verificationData.referredBy
        }
      },
      // Standard user profile fields
      preferences: {},
      videoResponses: {},
      profileLinks: {
        linkedin: verificationData.linkedinUrl
      },
      // Default notification preferences
      notificationPreferences: {
        email: {
          enabled: true
        },
        forum: {
          replyNotifications: true,
          likeNotifications: true,
          digestMode: false,
          digestTime: "09:00",
          enabled: true
        },
        emailInfo: {
          verified: true,
          address: userData.email,
          bounceCount: 0,
          lastBounce: null
        }
      },
      // Onboarding and admin flags
      onboardingComplete: false, // Waitlist users still need onboarding
      isAdmin: userData.email === 'grey@justburnt.com' || 
               userData.email === 'arjan@justburnt.com' || 
               userData.email === 'louie@justburnt.com',
      // Timestamps
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      lastUpdated: serverTimestamp()
    };
    
    await setDoc(profileRef, userProfileData);
    
    // Create profile summary document
    const profileSummaryRef = doc(db, "user_profile_summaries", userData.userId);
    const defaultProfileSummary = {
      userID: userData.userId,
      profileSummary: "",
      keyHighlights: {
        role: "",
        industries: [],
        strengths: []
      },
      idealRoleCharacteristics: {
        roleScope: "",
        keyMotivators: "",
        industryFocus: []
      },
      keySkills: [],
      painPoints: [],
      potentialFit: "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(profileSummaryRef, defaultProfileSummary);
    
    console.log(`Successfully created waitlist user profile for ${userData.userId}`);
    
    return {
      success: true,
      data: userProfileData
    };
  } catch (error) {
    console.error("Error creating waitlist user profile:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all waitlist users (admin only)
 */
export const getWaitlistUsers = async (adminEmail) => {
  try {
    // Validate admin permissions
    const adminEmails = ['grey@justburnt.com', 'arjan@justburnt.com', 'louie@justburnt.com'];
    if (!adminEmails.includes(adminEmail)) {
      return { success: false, error: 'Unauthorized: Admin access required', data: [] };
    }

    console.log(`Admin ${adminEmail} fetching waitlist users...`);
    
    const usersRef = collection(db, 'user_profiles');
    const q = query(
      usersRef,
      where('status', '==', 'waitlist')
    );
    
    const querySnapshot = await getDocs(q);
    
    const waitlistUsers = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      waitlistUsers.push({
        id: doc.id,
        uid: doc.id,
        ...userData
      });
    });
    
    console.log(`Retrieved ${waitlistUsers.length} waitlist users for admin ${adminEmail}`);
    
    return { success: true, data: waitlistUsers };
  } catch (error) {
    console.error('Error fetching waitlist users:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Approve waitlist user (change status from waitlist to active)
 */
export const approveWaitlistUser = async (userId, adminEmail) => {
  try {
    // Validate admin permissions
    const adminEmails = ['grey@justburnt.com', 'arjan@justburnt.com', 'louie@justburnt.com'];
    if (!adminEmails.includes(adminEmail)) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    console.log(`Admin ${adminEmail} approving waitlist user: ${userId}`);
    
    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const userData = userSnap.data();
    
    if (userData.status !== 'waitlist') {
      return { success: false, error: 'User is not on waitlist' };
    }
    
    // Update user status to active
    await updateDoc(userRef, {
      status: 'active',
      'waitlistMetadata.approvedAt': serverTimestamp(),
      'waitlistMetadata.approvedBy': adminEmail,
      lastUpdated: serverTimestamp()
    });
    
    console.log(`User ${userId} approved by admin ${adminEmail}`);
    return { success: true };
  } catch (error) {
    console.error('Error approving waitlist user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if user is on waitlist
 */
export const isUserOnWaitlist = async (userId) => {
  try {
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    if (!profileSnap.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const userData = profileSnap.data();
    
    return {
      success: true,
      data: {
        isWaitlist: userData.status === 'waitlist',
        status: userData.status || 'active',
        waitlistMetadata: userData.waitlistMetadata || null
      }
    };
  } catch (error) {
    console.error("Error checking waitlist status:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get waitlist statistics (admin only)
 */
export const getWaitlistStats = async (adminEmail) => {
  try {
    // Validate admin permissions
    const adminEmails = ['grey@justburnt.com', 'arjan@justburnt.com', 'louie@justburnt.com'];
    if (!adminEmails.includes(adminEmail)) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    const usersRef = collection(db, 'user_profiles');
    
    // Get waitlist users
    const waitlistQuery = query(usersRef, where('status', '==', 'waitlist'));
    const waitlistSnapshot = await getDocs(waitlistQuery);
    
    // Get active users
    const activeQuery = query(usersRef, where('status', '==', 'active'));
    const activeSnapshot = await getDocs(activeQuery);
    
    // Get users without status (legacy users, considered active)
    const allUsersSnapshot = await getDocs(usersRef);
    let legacyActiveCount = 0;
    
    allUsersSnapshot.forEach((doc) => {
      const userData = doc.data();
      if (!userData.status) {
        legacyActiveCount++;
      }
    });
    
    const stats = {
      waitlistCount: waitlistSnapshot.size,
      activeCount: activeSnapshot.size + legacyActiveCount,
      totalCount: allUsersSnapshot.size,
      conversionRate: waitlistSnapshot.size > 0 ? 
        ((activeSnapshot.size + legacyActiveCount) / (waitlistSnapshot.size + activeSnapshot.size + legacyActiveCount) * 100).toFixed(2) : 0
    };
    
    return { success: true, data: stats };
  } catch (error) {
    console.error('Error fetching waitlist stats:', error);
    return { success: false, error: error.message };
  }
};